import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useModalStack } from '@/context/ModalStackContext';
import {
  LayoutDashboard,
  FolderOpen,
  Calendar,
  Plus,
  Menu,
  X,
  User,
  Building2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import NewDocumentModal from '@/components/NewDocumentModal';
import AddTask from '@/components/AddTask';

interface AppLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { path: '/', label: 'לוח בקרה', icon: LayoutDashboard },
  { path: '/projects', label: 'פרויקטים', icon: FolderOpen },
  { path: '/calendar', label: 'לוח שנה', icon: Calendar },
  { path: '/account', label: 'חשבון', icon: User },
];

export default function AppLayout({ children }: AppLayoutProps) {

  const { isLoggedIn, logout, isInitialized } = useApp();
  const { registerModal } = useModalStack();

  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNewDoc, setShowNewDoc] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);

  // Register mobile sidebar in modal stack when open
  useEffect(() => {
    if (!sidebarOpen) return;
    return registerModal({
      id: 'mobile-sidebar',
      priority: 500,
      onClose: () => setSidebarOpen(false),
    });
  }, [sidebarOpen, registerModal]);

  

  const onClickShowNewDoc = () => {
    if (!isLoggedIn()) return;
    setShowNewDoc(true);
  }

  const onClickAddTask = () => {
    if (!isLoggedIn()) return;
    setShowAddTask(true);
  }

  const onLogout = () => {
    logout();
    setSidebarOpen(false);
  }

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">טוען...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn()) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 right-0 w-64 flex-col bg-sidebar border-l border-sidebar-border z-40">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-sidebar-border">
          <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-glow">
            <Building2 className="w-5 h-5 text-primary-foreground" onClick={onLogout} />
          </div>
          <div>
            <h1 className="font-bold text-sidebar-foreground text-lg">Field OS</h1>
            <p className="text-xs text-sidebar-foreground/60">מערכת פיקוח בנייה</p>
          </div>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-glow"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                )}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4">
          <button
            onClick={onClickShowNewDoc}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl gradient-primary text-primary-foreground font-medium shadow-glow hover:opacity-90 transition-opacity"
          >
            <Plus className="w-5 h-5" />
            <span>תיעוד חדש</span>
          </button>
        </div>
        <div className="p-4">
          <button
            onClick={onClickAddTask}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl gradient-primary text-primary-foreground font-medium shadow-glow hover:opacity-90 transition-opacity"
          >
            <Plus className="w-5 h-5" />
            <span>משימה חדשה</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed left-0 right-0 h-16 box-content bg-card border-b border-border flex items-center justify-between px-4 z-40 pt-safe-top">
        <button
          onClick={() => setSidebarOpen(true)}
          className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-secondary transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
            <Building2 className="w-4 h-4 text-primary-foreground" onClick={onLogout} />
          </div>
          <span className="font-bold">Field OS</span>
        </div>

        <div className="w-10" />
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-foreground/50 z-50"
          onClick={() => setSidebarOpen(false)}
        >
          <aside
            className="absolute inset-y-0 right-0 w-72 bg-sidebar animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-sidebar-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary-foreground" onClick={onLogout} />
                </div>
                <div>
                  <h1 className="font-bold text-sidebar-foreground text-lg">Field OS</h1>
                  <p className="text-xs text-sidebar-foreground/60">מערכת פיקוח בנייה</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-sidebar-accent transition-colors text-sidebar-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="px-4 py-6 space-y-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200",
                      isActive
                        ? "bg-sidebar-primary text-sidebar-primary-foreground"
                        : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-20 bg-card border-t border-border flex items-center justify-around px-4 z-40 pb-safe">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-colors",
                isActive ? "text-primary" : "text-muted-foreground"
              )}
            >
              <item.icon className="w-6 h-6" />
              <span className="text-xs font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Floating Action Button (Mobile) */}
      <button
        onClick={onClickShowNewDoc}
        className="md:hidden fixed bottom-24 right-4 w-14 h-14 rounded-full gradient-primary text-primary-foreground shadow-glow flex items-center justify-center z-40 hover:opacity-90 transition-opacity"
      >
        <Plus className="w-6 h-6" />
      </button>

      <button
        onClick={onClickAddTask}
        className="md:hidden fixed bottom-24 left-4 w-14 h-14 rounded-full gradient-secondary text-secondary-foreground shadow-glow flex items-center justify-center z-40 hover:opacity-90 transition-opacity"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Main Content */}
      <main className="md:mr-64 pt-[calc(4rem+env(safe-area-inset-top))] md:pt-0 pb-24 md:pb-0 min-h-screen">
        {children}
      </main>

      {/* New Document Modal */}
      <NewDocumentModal
        open={showNewDoc}
        onClose={() => setShowNewDoc(false)}
      />
      {/* Add Task Modal */}
      {showAddTask && (
        <AddTask
          open={showAddTask}
          onClose={() => setShowAddTask(false)}
        />)}
    </div>
  );
}
